import { useLocation, useNavigate } from 'react-router-dom';
import type { MouseEvent } from 'react';
import { Stack, Text } from '@repletoryreach/sdk-ui';
import { AppShell, useAppShell } from '@repletoryreach/sdk-ui/nav';
import { ADMIN_NAV, activeNavPath, type NavItem } from '../../lib/nav';
import { navIcon } from './nav-icons';
import { useT } from '../../i18n/I18nProvider';

export interface SidebarProps {
  /** Fires after a nav item is chosen: the mobile shell closes its drawer. */
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps = {}) {
  const t = useT();
  const { navbarMode } = useAppShell();
  const collapsed = navbarMode === 'icon';

  return (
    <Stack gap="lg" p={collapsed ? 'sm' : 'md'} style={{ minHeight: '100%' }}>
      {ADMIN_NAV.map((section) => (
        <Stack key={section.labelKey} gap={4}>
          {!collapsed && (
            // No `c` here on purpose. The rail is deep navy in both themes and
            // paints its own ink in shell.css; an inline colour from the muted
            // surface token beat that stylesheet and left these headings at
            // dark-on-dark.
            <Text role="overline" className="admin-shell__nav-section">
              {t(section.labelKey)}
            </Text>
          )}
          <Stack gap={2}>
            {section.items.map((item) => (
              <NavRow key={item.path} item={item} onNavigate={onNavigate} />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

function NavRow({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  // One item lit at a time: the page's own item wins over its parent (4 October 2026).
  const { pathname } = useLocation();
  const active = activeNavPath(pathname) === item.path;
  const label = t(item.labelKey);

  // The row is a real anchor with a real href. Without one the sdk-ui NavLink
  // renders `<a href={undefined}>`, which is not in the tab order, so the whole
  // menu was unreachable from the keyboard and no item could be opened in a new
  // tab. Routing still happens in the click handler; the modifier clicks and
  // the middle click fall through to the browser, which is what a link is for.
  function handleClick(e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    navigate(item.path);
    onNavigate?.();
  }

  // `workstream` used to render as an outline badge on the row. "W15" and
  // "soon" are our planning vocabulary, not the operator's, so the note stays
  // in lib/nav.ts and off the screen.
  return (
    <AppShell.NavLink
      label={label}
      description={item.descriptionKey ? t(item.descriptionKey) : undefined}
      leftSection={navIcon(item.path)}
      href={item.path}
      active={active}
      onClick={handleClick}
      iconLabel={label}
    />
  );
}
